import { BulkActionsContextProvider } from 'customer-data-bulk-actions/contexts/bulkActionsContext';
import { TASK_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
// @ts-expect-error untyped
import ProvideReferenceResolvers from 'reference-resolvers/ProvideReferenceResolvers';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { columnNamesToRequestFormat } from '../../api/getAssociationsByTaskIds';
import { updateDefaultView } from '../../api/views';
import SearchBox from '../../components/filters/SearchBox';
import DefaultEmptyState from '../../components/table/emptyState/DefaultEmptyState';
import NoTasksMatchFiltersEmptyState from '../../components/table/emptyState/NoTasksMatchFiltersEmptyState';
import TasksTableV2 from '../../components/table/TasksTableV2';
import EditColumnsButton from '../../components/views/EditColumnsButton';
import { COLUMN_EDITOR_CONTAINER_MODAL } from '../../constants/modalTypes';
import { TABLE_PROPERTIES } from '../../constants/properties';
import { ALL_PREDEFINED_COLUMNS, MINIMUM_COLUMN_WIDTH, STATIC_COLUMNS_FOR_OPEN_VIEWS } from '../../constants/table';
import { DEFAULT } from '../../constants/views';
import { useDispatch, useSelector } from '../../context/Redux';
import TaskViewTableContextProvider from '../../context/TaskViewTableContextProvider';
import { useUserGates, useUserScopes } from '../../hooks/auth';
import { usePropertiesWithTimezoneDefinition } from '../../hooks/properties';
import { useColumnWidths, usePagination, useSortTable } from '../../hooks/table';
import { useHydratedTaskSearchResults, useSearchRequestOptionsForView } from '../../hooks/tasksSearchResults';
import { useActiveView } from '../../hooks/useActiveView';
import { useCustomAssociationColumnNames } from '../../hooks/useCustomAssociationColumnNames';
import { useHandleSpecialQueryParams } from '../../hooks/useHandleSpecialQueryParams';
import { useTableColumns } from '../../hooks/useTableColumns';
import { openModalAction } from '../../redux/actions/overlay';
import { setViewColumns } from '../../redux/actions/tempViewState';
import { getHasNonDefaultFiltersSelector } from '../../redux/selectors/tempViewState';
import { createTaskAssociatedObjectsReferenceResolver, TASK_ASSOCIATION_CACHE_KEY
// @ts-expect-error untyped
} from '../../reference-resolvers/TaskAssociatedObjectsReferenceResolver';
import { trackSavedViewFiltersColumnsUpdated } from '../../tracking/tracker';
import { isCustomObjectAssociationColumnName } from '../../utils/associationColumnNames';
import { getMemoizedColumnsV2 } from '../../utils/tableV2';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SEARCH_BAR_MIN_WIDTH = '200px';
export default function TaskViewTableV2({
  viewId,
  onRefreshSearch,
  onDelayedRefreshSearch,
  loading: graphQLSearchIsLoading
}) {
  const {
    loading: activeViewLoading,
    error,
    data: {
      view: activeView
    } = {
      view: {}
    }
  } = useActiveView();
  const dispatch = useDispatch();
  const hasHandledQueryParams = useHandleSpecialQueryParams();
  const {
    searchKey
  } = useSearchRequestOptionsForView(viewId);
  const userGates = useUserGates();
  const userScopes = useUserScopes();
  const {
    loading: propertiesLoading,
    properties: propertiesWithTimezoneDefinition,
    error: propertiesError
  } = usePropertiesWithTimezoneDefinition(TABLE_PROPERTIES);
  const {
    columnNames
  } = useTableColumns(viewId);
  const customObjectAssociationColumnNames = useCustomAssociationColumnNames();
  const filteredColumnNames = useMemo(() => {
    return columnNames.filter(columnName => ALL_PREDEFINED_COLUMNS.includes(columnName) || isCustomObjectAssociationColumnName(columnName) &&
    // columnNames comes from user attributes so want to filter out custom object column names that the user may have been using in other portals
    customObjectAssociationColumnNames.has(columnName));
  }, [columnNames, customObjectAssociationColumnNames]);
  const ASSOCIATION_SPECS_FOR_RESOLVER = useMemo(() => {
    return columnNamesToRequestFormat(filteredColumnNames);
  }, [filteredColumnNames]);
  const {
    tasks,
    total: totalSearchResults,
    error: searchResultsError,
    uninitialized: tasksSearchResultsUninitialized,
    loading: tasksSearchResultsLoading
  } = useHydratedTaskSearchResults(searchKey);
  const tasksToShowWhileLoading = useRef([]);
  useEffect(() => {
    if (!tasksSearchResultsLoading) {
      tasksToShowWhileLoading.current = tasks;
    }
  }, [tasks, tasksSearchResultsLoading]);
  const {
    handleSortTable,
    sortState: {
      propertyName: sortByPropertyName,
      sortOrder
    }
  } = useSortTable(viewId);
  const handleSortChange = ({
    nextSorts
  }) => {
    if (nextSorts && nextSorts[0]) {
      const sort = nextSorts[0];
      handleSortTable({
        property: sort.columnName,
        order: sort.direction === 1 ? 'DESC' : 'ASC'
      });
    }
  };
  const {
    pageIndex,
    setPageIndex,
    pageSize,
    onSetTaskTablePageSize
  } = usePagination(viewId);
  const getHasNonDefaultFilters = useSelector(getHasNonDefaultFiltersSelector);
  const handleClickEditColumnButton = useCallback(() => {
    dispatch(openModalAction({
      modalData: {},
      modalType: COLUMN_EDITOR_CONTAINER_MODAL
    }));
  }, [dispatch]);
  const hasGate = useCallback(gate => userGates.includes(gate), [userGates]);
  const hasScope = useCallback(scope => userScopes.includes(scope), [userScopes]);
  const handleColumnsChange = useCallback(newColumnNames => {
    const columnNamesToSave = newColumnNames.map(columnName => ({
      name: columnName
    }));
    dispatch(setViewColumns({
      columns: columnNamesToSave,
      viewId
    }));
    trackSavedViewFiltersColumnsUpdated({
      id: viewId,
      columns: newColumnNames
    });

    // If default (non-user defined) view, save column preferences
    if (!activeViewLoading && !error && activeView.type === DEFAULT) {
      updateDefaultView({
        view: {
          id: viewId,
          columns: columnNamesToSave
        }
      }).catch(err => {
        setTimeout(() => {
          throw err;
        });
      });
    }
  }, [activeView, activeViewLoading, dispatch, error, viewId]);
  const emptyState = getHasNonDefaultFilters(viewId) ? /*#__PURE__*/_jsx(NoTasksMatchFiltersEmptyState, {}) : /*#__PURE__*/_jsx(DefaultEmptyState, {});
  const baseDataLoading = propertiesLoading || !propertiesWithTimezoneDefinition || !hasHandledQueryParams || activeViewLoading;
  const tasksLoading = !tasks && (tasksSearchResultsUninitialized || graphQLSearchIsLoading);
  const {
    columnWidths,
    setColumnWidths
  } = useColumnWidths();

  // This is a temporary fix to migrate column widths from localStorage to superstore
  useEffect(() => {
    try {
      const columnWidthsFromLocalStorageString = localStorage.getItem('TASKS_HOME_PAGE_TABLE');
      if (columnWidthsFromLocalStorageString) {
        const columnWidthsFromLocalStorage = JSON.parse(columnWidthsFromLocalStorageString);
        Object.keys(columnWidthsFromLocalStorage).forEach(columnName => {
          setColumnWidths(columnName, Math.max(columnWidthsFromLocalStorage[columnName], MINIMUM_COLUMN_WIDTH));
        });
        localStorage.removeItem('TASKS_HOME_PAGE_TABLE');
      }
    } catch (err) {
      // Do nothing
    }
  }, [setColumnWidths]);
  const handleColumnWidthChange = useCallback(({
    columnName,
    nextWidth
  }) => {
    setColumnWidths(columnName, nextWidth);
  }, [setColumnWidths]);
  const columns = useMemo(() => {
    const columnOptions = {
      columnNames,
      customAssociationColumnNamesToDefinitions: customObjectAssociationColumnNames,
      properties: propertiesWithTimezoneDefinition,
      staticColumns: STATIC_COLUMNS_FOR_OPEN_VIEWS,
      columnWidths
    };
    return getMemoizedColumnsV2(columnOptions);
  }, [columnNames, customObjectAssociationColumnNames, propertiesWithTimezoneDefinition, columnWidths]);
  const TasksTableWithResolver = useMemo(() => {
    return ProvideReferenceResolvers({
      [TASK_ASSOCIATION_CACHE_KEY]: createTaskAssociatedObjectsReferenceResolver({
        fetchOptions: {
          associationSpecs: ASSOCIATION_SPECS_FOR_RESOLVER
        }
      })
    }, TasksTableV2, {
      mergeResolvers: true
    });
  }, [ASSOCIATION_SPECS_FOR_RESOLVER]);
  if (propertiesError || searchResultsError || error) {
    return /*#__PURE__*/_jsxs(UIErrorMessage, {
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: "TASKS_TABLE_ERROR"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "tasksAlerts.genericError"
      })]
    });
  }

  // Wait for CrmSearch results
  // Check uninitialized property as there may be delay between GraphQL query finishing and results loading into Redux
  // Wait for properties to load as we need them for some column headers
  if (tasksLoading || baseDataLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      layout: "centered",
      grow: true,
      "data-selenium-test": "tasks-table-loading"
    });
  }
  const tasksToRender = tasksSearchResultsLoading ? tasksToShowWhileLoading.current : tasks;
  return /*#__PURE__*/_jsx(BulkActionsContextProvider, {
    objectTypeId: TASK_TYPE_ID,
    children: /*#__PURE__*/_jsx(TaskViewTableContextProvider, {
      viewId: viewId,
      onRefreshSearch: onRefreshSearch,
      onDelayedRefreshSearch: onDelayedRefreshSearch,
      children: /*#__PURE__*/_jsx(TasksTableWithResolver, {
        hasGate: hasGate,
        hasScope: hasScope,
        headerLeftSlot: /*#__PURE__*/_jsx(Flex, {
          minInlineSize: SEARCH_BAR_MIN_WIDTH,
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(SearchBox, {
            use: "on-dark"
          })
        }),
        headerRightSlot: /*#__PURE__*/_jsx(View, {
          paddingInlineEnd: "400",
          children: /*#__PURE__*/_jsx(EditColumnsButton, {
            hideIcon: true,
            onClick: handleClickEditColumnButton,
            size: "extra-small",
            use: "tertiary-extra-light"
          })
        }),
        noRowsSlot: emptyState,
        onColumnWidthChange: handleColumnWidthChange,
        onColumnsChange: handleColumnsChange,
        onPageIndexChange: setPageIndex,
        onPageSizeChange: onSetTaskTablePageSize,
        onSortsChange: handleSortChange,
        pageIndex: pageIndex,
        pageSize: pageSize,
        sorts: [{
          columnName: sortByPropertyName,
          direction: sortOrder === 'DESC' ? 1 : -1
        }],
        tasks: tasksToRender,
        totalRowCount: totalSearchResults,
        viewId: viewId,
        loading: graphQLSearchIsLoading,
        columns: columns
      })
    })
  });
}